import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { portalApi } from '@/api/portal'
import { Button } from '@/components/ui/button'
import { Star } from 'lucide-react'
import { usePortalDates } from '@/hooks/useDates'

export interface FeedbackableBooking {
  id: string
  courseName: string
  bookingDate: string   // DateOnly string "YYYY-MM-DD"
  hasFeedback: boolean
  feedbackRating: number | null
}

export default function FeedbackModal({
  booking,
  onClose,
}: {
  booking: FeedbackableBooking
  onClose: () => void
}) {
  const { formatDate } = usePortalDates()
  const queryClient = useQueryClient()
  const [rating, setRating] = useState(booking.feedbackRating ?? 0)
  const [hovered, setHovered] = useState(0)
  const [comment, setComment] = useState('')

  const { data: existing, isLoading: loadingExisting } = useQuery({
    queryKey: ['portal-booking-feedback', booking.id],
    queryFn: () => portalApi.getBookingFeedback(booking.id).then(r => r.data),
    enabled: booking.hasFeedback,
  })

  if (existing && comment === '' && existing.comment) setComment(existing.comment)
  if (existing && rating === 0 && booking.feedbackRating) setRating(booking.feedbackRating)

  const mutation = useMutation({
    mutationFn: () => portalApi.submitFeedback(booking.id, { rating, comment: comment || null }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['portal-bookings'] })
      queryClient.invalidateQueries({ queryKey: ['portal-dashboard'] })
      queryClient.invalidateQueries({ queryKey: ['portal-booking-feedback', booking.id] })
      onClose()
    },
  })

  const displayRating = hovered || rating
  const isEditing = booking.hasFeedback
  const dateLabel = formatDate(booking.bookingDate + 'T00:00:00', {
    day: 'numeric', month: 'short', year: 'numeric',
  })

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-md">
        <div className="px-6 pt-5 pb-4 border-b border-gray-100">
          <h2 className="text-base font-semibold text-gray-900">
            {isEditing ? 'Edit your feedback' : 'Rate your training'}
          </h2>
          <p className="text-sm text-gray-500 mt-0.5">
            {booking.courseName} · {dateLabel}
          </p>
        </div>

        {loadingExisting ? (
          <div className="px-6 py-8 text-center text-sm text-gray-400">Loading…</div>
        ) : (
          <div className="px-6 py-5 space-y-5">
            <div>
              <p className="text-sm font-medium text-gray-700 mb-2">Your rating</p>
              <div className="flex items-center gap-1">
                {Array.from({ length: 10 }).map((_, i) => (
                  <button
                    key={i}
                    type="button"
                    onMouseEnter={() => setHovered(i + 1)}
                    onMouseLeave={() => setHovered(0)}
                    onClick={() => setRating(i + 1)}
                    className="cursor-pointer transition-transform hover:scale-110 focus:outline-none"
                  >
                    <Star
                      size={24}
                      className={i < displayRating ? 'text-amber-400 fill-amber-400' : 'text-gray-200 fill-gray-200'}
                    />
                  </button>
                ))}
                {displayRating > 0 && (
                  <span className="ml-2 text-sm font-semibold text-gray-600">{displayRating}/10</span>
                )}
              </div>
            </div>

            <div className="space-y-1.5">
              <label className="text-sm font-medium text-gray-700">Comment (optional)</label>
              <textarea
                value={comment}
                onChange={e => setComment(e.target.value)}
                rows={3}
                maxLength={2000}
                placeholder="Tell us how the session went…"
                className="w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring resize-none"
              />
            </div>
          </div>
        )}

        <div className="px-6 py-4 border-t border-gray-100 flex justify-end gap-2">
          <Button variant="outline" size="sm" onClick={onClose}>Cancel</Button>
          <Button
            size="sm"
            onClick={() => mutation.mutate()}
            disabled={rating === 0 || mutation.isPending || loadingExisting}
          >
            {mutation.isPending ? 'Saving…' : isEditing ? 'Update feedback' : 'Submit feedback'}
          </Button>
        </div>
      </div>
    </div>
  )
}
